import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './Carousel.css'; // Use lowercase to match your existing file

const Carousel = ({ slides }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const navigate = useNavigate();
  const timerRef = useRef(null);

  // Function to reset and start the timer
  const resetTimer = () => {
    // Clear existing timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    
    // Set new timer
    timerRef.current = setInterval(() => {
      setCurrentSlide((prevSlide) => (prevSlide === slides.length - 1 ? 0 : prevSlide + 1));
    }, 12000); // Change slide every 12 seconds
  };

  // Initial timer setup
  useEffect(() => {
    resetTimer();
    
    // Cleanup on component unmount
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [slides.length]);

  // Effect to watch for currentSlide changes
  useEffect(() => {
    resetTimer();
  }, [currentSlide]);

  // Manual navigation
  const goToSlide = (index) => {
    if (isTransitioning || index === currentSlide) return;
    setCurrentSlide(index);
    // No need to call resetTimer here as it will be triggered by the useEffect watching currentSlide
  };

  const nextSlide = () => {
    setCurrentSlide((prevSlide) => (prevSlide === slides.length - 1 ? 0 : prevSlide + 1));
    // No need to call resetTimer here as it will be triggered by the useEffect watching currentSlide
  };

  const prevSlide = () => {
    setCurrentSlide((prevSlide) => (prevSlide === 0 ? slides.length - 1 : prevSlide - 1));
    // No need to call resetTimer here as it will be triggered by the useEffect watching currentSlide
  };

  const handleButtonClick = (index) => {
    // Define paths for each slide explicitly
    switch(index) {
      case 0:
        navigate('/thermals/rh25v2');
        break;
      case 1:
        navigate('/night-vision');
        break;
      case 2:
        navigate('/thermals');
        break;
      default:
        navigate('/');
    }
  };

  return (
    <div className="carousel-container">
      {/* Gradient overlay positioned behind content */}
      <div className="carousel-gradient-overlay"></div>
      
      {/* Navigation arrows */}
      <button className="carousel-arrow carousel-arrow-left" onClick={prevSlide}>
        <span>&#10094;</span>
      </button>
      <button className="carousel-arrow carousel-arrow-right" onClick={nextSlide}>
        <span>&#10095;</span>
      </button>
      
      <div className="carousel-slides">
        {slides.map((slide, index) => (
          <div 
            key={index} 
            className={`carousel-slide ${index === currentSlide ? 'active' : ''}`}
          >
            <div className="slide-content">
              <h2 className="slide-title">{slide.title}</h2>
              {slide.subtitle && <h3 className="slide-subtitle">{slide.subtitle}</h3>}
              <p className="slide-description">{slide.description}</p>
              {slide.buttonText && (
                <button 
                  className="slide-button" 
                  onClick={() => handleButtonClick(index)}
                  style={{
                    // Ensure button is always clickable regardless of slide state
                    position: 'relative',
                    zIndex: 5
                  }}
                >
                  {slide.buttonText}
                  <span className="button-arrow">&#8594;</span>
                </button>
              )}
            </div>
            <div className="slide-image">
              <img 
                src={slide.image} 
                alt={slide.title} 
                className="slide-img-element"
              />
              <div className="image-glow-effect"></div>
            </div>
          </div>
        ))}
      </div>
      
      <div className="carousel-indicators">
        {slides.map((_, index) => (
          <span 
            key={index} 
            className={`indicator ${index === currentSlide ? 'active' : ''}`} 
            onClick={() => goToSlide(index)}
          />
        ))}
      </div>
    </div>
  );
};

export default Carousel;